import { Search } from "@mui/icons-material";
import {
  Autocomplete,
  GlobalStyles,
  InputAdornment,
  TextField,
} from "@mui/material";
import { useState } from "react";

// Busca das telas de configuração em accordion. Cada seção é um Accordion com
// `data-secao` e cada item um elemento com `data-busca` dentro dele. O índice
// (`secoes`) é fixo e fica na tela que usa a busca: as seções só carregam ao
// abrir, então o conteúdo ainda não existe na tela quando se busca (e varrer
// tudo traria de volta as consultas ao ERP na entrada da página).
// Seção: { secao, titulo, termos, itens: [[id, titulo, termos]], visivel? }.

const normalizar = (texto) =>
  String(texto || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

// Uma opção por seção (abre a seção) e uma por item (abre e destaca o item).
function montarOpcoes(secoes) {
  return secoes
    .filter((s) => !s.visivel || s.visivel())
    .flatMap((s) => [
      {
        chave: s.secao,
        secao: s.secao,
        grupo: s.titulo,
        titulo: s.titulo,
        busca: normalizar(`${s.titulo} ${s.termos}`),
      },
      ...s.itens.map(([id, titulo, termos]) => ({
        chave: id,
        secao: s.secao,
        id,
        grupo: s.titulo,
        titulo,
        busca: normalizar(`${s.titulo} ${titulo} ${termos}`),
      })),
    ]);
}

// Todas as palavras digitadas precisam aparecer (em qualquer ordem).
function filtrar(opcoes, { inputValue }) {
  const palavras = normalizar(inputValue).split(/\s+/).filter(Boolean);
  return opcoes.filter((o) => palavras.every((p) => o.busca.includes(p)));
}

function destacar(elemento) {
  const centralizar = () =>
    elemento.scrollIntoView({ behavior: "smooth", block: "center" });
  centralizar();
  // De novo depois que tabelas/listas recém-carregadas terminam de crescer.
  setTimeout(centralizar, 700);
  elemento.classList.add("busca-destaque");
  setTimeout(() => elemento.classList.remove("busca-destaque"), 2500);
}

// Abre o accordion (o clique dispara a carga sob demanda da seção) e espera o
// carregamento acabar; sem o item na tela (ex.: nenhuma configuração
// cadastrada), destaca o título da seção.
export function irPara({ secao, id }) {
  const accordion = document.querySelector(`[data-secao="${secao}"]`);
  if (!accordion) return;
  const titulo = accordion.querySelector(":scope > .MuiAccordionSummary-root");
  if (!accordion.classList.contains("Mui-expanded")) titulo?.click();

  const inicio = Date.now();
  const timer = setInterval(() => {
    const decorrido = Date.now() - inicio;
    // Rolar durante a animação de abrir deixa o item fora do lugar.
    const esperando =
      accordion.querySelector(".MuiCircularProgress-root") ||
      !accordion.querySelector(":scope > .MuiCollapse-entered");
    // 400ms: tempo de a seção iniciar a carga; 20s: limite para ERP lento (o
    // mesmo connectionTimeout do pool da HubSoft).
    if (decorrido < 400 || (esperando && decorrido < 20000)) return;
    clearInterval(timer);
    destacar((id && accordion.querySelector(`[data-busca="${id}"]`)) || titulo);
  }, 150);
}

export default function BuscaConfiguracao({ secoes, placeholder }) {
  const [texto, setTexto] = useState("");

  return (
    <>
      {/* Item encontrado pela busca (classe posta por destacar). */}
      <GlobalStyles
        styles={(theme) => ({
          ".busca-destaque": {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: "4px",
            borderRadius: "4px",
          },
        })}
      />
      <Autocomplete
        options={montarOpcoes(secoes)}
        value={null}
        inputValue={texto}
        onInputChange={(_, valor, motivo) =>
          motivo !== "reset" && setTexto(valor)
        }
        onChange={(_, opcao) => {
          if (!opcao) return;
          setTexto("");
          irPara(opcao);
        }}
        filterOptions={filtrar}
        groupBy={(o) => o.grupo}
        getOptionLabel={(o) => o.titulo}
        isOptionEqualToValue={(o, v) => o.chave === v.chave}
        renderOption={(props, o) => {
          const { key, ...resto } = props;
          return (
            <li key={key} {...resto} style={{ fontSize: "0.85rem" }}>
              {o.id ? o.titulo : "Abrir seção"}
            </li>
          );
        }}
        noOptionsText="Nenhuma configuração encontrada"
        blurOnSelect
        size="small"
        sx={{ mb: 1.5, maxWidth: 520 }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </>
  );
}
